import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAxiosSwagger } from '../../hooks/useAxiosSwagger';
import {
    copyScenarioRunDiagnostics,
    diagnosticsFileName,
    fetchScenarioRunDiagnostics,
    saveScenarioRunDiagnostics,
} from '../../api/scenarioRunDiagnostics';

interface IScenarioRunDiagnosticsProps {
    customer: string;
    environment: string;
    runId: string;
}

type Feedback = { kind: 'ok' | 'warn' | 'error'; text: string };

const sizeOf = (text: string) => {
    const kilobytes = new Blob([text]).size / 1024;

    return kilobytes < 1024
        ? `${Math.max(1, Math.round(kilobytes))} KB`
        : `${(kilobytes / 1024).toFixed(1)} MB`;
};

const failureOf = (error: any) => {
    const body = error?.response?.data;

    return typeof body === 'string' && body
        ? body
        : 'The diagnostics of this run could not be put together.';
};

/**
 * Hands the whole run over to somebody who cannot open this page: copied to the clipboard to paste into a chat,
 * or saved as a file to attach or point at.
 */
export const ScenarioRunDiagnostics = ({
    customer,
    environment,
    runId,
}: IScenarioRunDiagnosticsProps) => {
    const axios = useAxiosSwagger();

    const [busy, setBusy] = useState<'copy' | 'download' | undefined>();
    const [feedback, setFeedback] = useState<Feedback | undefined>();

    const load = () => {
        if (!axios) {
            return Promise.reject(new Error('Not signed in yet.'));
        }

        return fetchScenarioRunDiagnostics(axios, customer, environment, runId);
    };

    const copy = async () => {
        setBusy('copy');
        setFeedback(undefined);

        const content = load();

        // Handled below; without this a refused clipboard would also report the content as unhandled.
        content.catch(() => undefined);

        try {
            await copyScenarioRunDiagnostics(content);

            setFeedback({
                kind: 'ok',
                text: `Copied ${sizeOf(
                    await content
                )}. Paste it into the chat.`,
            });
        } catch {
            // Either the bundle could not be fetched, or the browser refused the clipboard. Only the second is
            // worth a file instead.
            try {
                const text = await content;

                saveScenarioRunDiagnostics(text, runId);
                setFeedback({
                    kind: 'warn',
                    text: `The browser refused the clipboard, so it was saved as ${diagnosticsFileName(
                        runId
                    )} instead.`,
                });
            } catch (error) {
                setFeedback({ kind: 'error', text: failureOf(error) });
            }
        } finally {
            setBusy(undefined);
        }
    };

    const download = async () => {
        setBusy('download');
        setFeedback(undefined);

        try {
            const text = await load();

            saveScenarioRunDiagnostics(text, runId);
            setFeedback({
                kind: 'ok',
                text: `Saved ${diagnosticsFileName(runId)} (${sizeOf(text)}).`,
            });
        } catch (error) {
            setFeedback({ kind: 'error', text: failureOf(error) });
        } finally {
            setBusy(undefined);
        }
    };

    const hint =
        'Everything about this run in one JSON document: the run as the runner wrote it, the suite it ran, ' +
        'and what each scenario read back from the connector, Chargebee and Exact. Credentials are masked; ' +
        'customer data is not, so share it only where that is fine.';

    return (
        <div className="arl-diagnostics">
            <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={copy}
                disabled={!!busy}
                title={hint}
            >
                <FontAwesomeIcon
                    icon={busy === 'copy' ? 'spinner' : 'copy'}
                    spin={busy === 'copy'}
                    className="me-2"
                />
                Copy diagnostics
            </button>
            <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={download}
                disabled={!!busy}
                title={hint}
            >
                <FontAwesomeIcon
                    icon={busy === 'download' ? 'spinner' : 'cloud-arrow-down'}
                    spin={busy === 'download'}
                    className="me-2"
                />
                Download
            </button>
            {feedback && (
                <span
                    className={`arl-diagnostics__feedback arl-diagnostics__feedback--${feedback.kind}`}
                    role="status"
                >
                    {feedback.kind === 'ok' && (
                        <FontAwesomeIcon icon="check" className="me-1" />
                    )}
                    {feedback.text}
                </span>
            )}
        </div>
    );
};
